import { PlannerConfig, FeatureConfig, ConfigOptions } from '../@types';
import { bestaComponentResolver } from '@inter-ikea-storageone/range-besta';
import { plannerComponentResolver } from '@inter-ikea-storageone/range';
import bestaCatalog from '@inter-ikea-storageone/range-besta/data/catalog.json';
import bestaTemplate from '@inter-ikea-storageone/range-besta/data/template.json';
import bestaFrontLayoutsCatalog from '@inter-ikea-storageone/range-besta/data/front-layouts/catalog.json';
import bestaFrontLayoutsTemplate from '@inter-ikea-storageone/range-besta/data/front-layouts/template.json';
import mediaCatalog from '@inter-ikea-storageone/range-besta/data/media/catalog.json';
import mediaTemplate from '@inter-ikea-storageone/range-besta/data/media/template.json';
import proppingCatalog from '@inter-ikea-storageone/range-besta-eket-propping/data/catalog.json';
import proppingTemplate from '@inter-ikea-storageone/range-besta-eket-propping/data/template.json';
import eketCatalog from '@inter-ikea-storageone/range-eket/data/catalog.json';
import eketTemplate from '@inter-ikea-storageone/range-eket/data/template.json';
import frames from './productGroups/frames';
import fronts from './productGroups/fronts';
import handles from './productGroups/handles';
import legs from './productGroups/legs';
import interiors from './productGroups/interiors';
import topPanels from './productGroups/topPanels';
import eketFrames from '../eket/productGroups/frames';
import eketMediaFrames from '../eket/productGroups/media';
import eketLegs from '../eket/productGroups/legs';
import lights from './productGroups/lights';
import BestaCameraBehavior from './camera/BestaCameraBehavior';
import { ToggleDoorsFeature as ToggleDoors } from 'rangeConfig/shared/features/ToggleHingeDoors';
import platform from 'rangeConfig/shared/utils/platform';
import { eketComponentResolver } from '@inter-ikea-storageone/range-eket';
import { LightsSelection } from 'rangeConfig/shared/features/LightsSelection';
import { getOptimizedModels } from 'rangeConfig/shared/utils/optimizedModels';
import handlesCatalog from '@inter-ikea-storageone/range-handles/data/catalog.json';
import handlesTemplate from '@inter-ikea-storageone/range-handles/data/template.json';
import MeasureAction from 'components/SceneMenu/actions/MeasureAction';
import DoorsAction from 'components/SceneMenu/actions/DoorsAction';
import accessories from './productGroups/accessories';
import ProppingAction from 'components/SceneMenu/actions/ProppingAction';
import { HandlesDrillTemplate } from 'rangeConfig/shared/features/HandlesDrillTemplate/HandlesDrillTemplate';
import { NightLightsFeature } from 'rangeConfig/shared/features/NightLights/NightLightsFeature';
import { TooManyLightsFeature } from 'rangeConfig/shared/features/TooManyLights/TooManyLightsFeature';
import { FrontsOpeningFunctionFeature } from 'rangeConfig/besta/features/FrontsOpeningFunction/FrontsOpeningFunctionFeature';
import { PushToOpenFeature } from 'rangeConfig/besta/features/PushToOpen/PushToOpenFeature';
import sprToVpcMap from '@inter-ikea-storageone/range-besta/data/spr2vpc.json';
import bestaLandingPageConfig from './bestaLandingPageConfig';
import { DistanceMeasurementFeature } from 'rangeConfig/shared/features/DistanceMeasurementFeature';
import { TopPanelHeightInfoFeature } from 'rangeConfig/besta/features/TopPanelHeightInfo/TopPanelHeightInfoFeature';
import { MultiWallSeeThroughFeature } from 'rangeConfig/shared/features/MultiWallSeeThroughFeature';
import { TVFeature } from 'rangeConfig/shared/features/TVFeature/TVFeature';

const defaultAssetsPath = '/assets/';
const assetsPath = import.meta.env.REACT_APP_ASSETS_PATH || defaultAssetsPath;
const screensaverPath = `${assetsPath}screensaver/`;
const mainScreensaverPath = `${screensaverPath}screensaver_BESTA_light_long.mp4`;
const secondaryScreensaverPath = `${screensaverPath}screensaver_BESTA_dark_long.mp4`;

const bestaPlannerConfig = (options?: ConfigOptions): PlannerConfig => {
  const features = (): { mobile: FeatureConfig[]; desktop: FeatureConfig[] } => {
    return {
      mobile: [
        { id: 'MultiwallSeeThroughFeature', feature: MultiWallSeeThroughFeature },
        { id: 'LightsSelection', feature: LightsSelection },
        { id: 'ToggleDoors', feature: ToggleDoors },
        { id: 'HandlesDrillTemplate', feature: HandlesDrillTemplate },
        { id: 'TooManyLights', feature: TooManyLightsFeature },
        { id: 'FrontsOpeningFunction', feature: FrontsOpeningFunctionFeature },
        { id: 'PushToOpen', feature: PushToOpenFeature },
        { id: 'TopPanelHeightInfoFeature', feature: TopPanelHeightInfoFeature },
        { id: 'TVFeature', feature: TVFeature }
      ],
      desktop: [
        { id: 'MultiwallSeeThroughFeature', feature: MultiWallSeeThroughFeature },
        { id: 'LightsSelection', feature: LightsSelection },
        { id: 'ToggleDoors', feature: ToggleDoors },
        { id: 'HandlesDrillTemplate', feature: HandlesDrillTemplate },
        { id: 'NightLights', feature: NightLightsFeature },
        { id: 'TooManyLights', feature: TooManyLightsFeature },
        { id: 'FrontsOpeningFunction', feature: FrontsOpeningFunctionFeature },
        { id: 'PushToOpen', feature: PushToOpenFeature },
        { id: 'DistanceMeasurement', feature: DistanceMeasurementFeature },
        { id: 'TopPanelHeightInfoFeature', feature: TopPanelHeightInfoFeature },
        { id: 'TVFeature', feature: TVFeature }
      ]
    };
  };

  const productGroups = () => {
    const productGroups = {
      desktop: [
        frames,
        fronts,
        handles,
        topPanels,
        legs,
        interiors,
        accessories,
        lights,
        eketFrames,
        eketLegs
      ],
      mobile: [
        frames,
        fronts,
        handles,
        topPanels,
        legs,
        interiors,
        accessories,
        lights,
        eketFrames,
        eketMediaFrames,
        eketLegs
      ]
    };

    return productGroups[platform].map(group => group());
  };

  const defaultCatalogs = [
    {
      name: 'besta',
      catalog: getOptimizedModels(bestaCatalog),
      template: bestaTemplate,
      assetsPath
    },
    {
      name: 'besta-front-layouts',
      catalog: bestaFrontLayoutsCatalog,
      template: bestaFrontLayoutsTemplate,
      assetsPath
    },
    {
      name: 'media',
      catalog: getOptimizedModels(mediaCatalog),
      template: mediaTemplate,
      assetsPath
    },
    {
      name: 'handles',
      catalog: getOptimizedModels(handlesCatalog),
      template: handlesTemplate,
      assetsPath
    },
    {
      name: 'eket',
      catalog: getOptimizedModels(eketCatalog),
      template: eketTemplate,
      assetsPath
    }
  ];

  const sceneActions = (usePropping: boolean) => ({
    desktop: usePropping
      ? [MeasureAction, DoorsAction, ProppingAction]
      : [MeasureAction, DoorsAction],
    mobile: [DoorsAction, MeasureAction]
  });

  const usePropping = !!options?.featureFlags?.bestaPropping;
  return {
    componentResolvers: [plannerComponentResolver, bestaComponentResolver, eketComponentResolver],
    productCatalogs: usePropping
      ? [
          ...defaultCatalogs,
          {
            name: 'propping',
            catalog: getOptimizedModels(proppingCatalog),
            template: proppingTemplate,
            assetsPath
          }
        ]
      : defaultCatalogs,
    sprToVpcMap,
    screensaverPath: mainScreensaverPath,
    secondaryScreensaverPath: secondaryScreensaverPath,
    cameraBehavior: BestaCameraBehavior,
    productGroups: productGroups(),
    features: features()[platform],
    sceneActions: sceneActions(usePropping)[platform],
    landingPageConfig: bestaLandingPageConfig,
    featureToggles: []
  };
};

export default bestaPlannerConfig;
